import { createSlice } from '@reduxjs/toolkit';

interface BookingState {
    bookings: any[];
    bookingsListParams: {
        total: number;
        page: number;
        per_page: number;
    };
    currentBooking: any | null;
    loading: boolean;
    importedBookings: any[];
    jobStatus: {
        jobId: string | null;
        status: string | null;
        progress: number;
        error: string | null;
    };
    deleteBookingPaymentLoading: boolean;
}

const initialState: BookingState = {
    bookings: [],
    bookingsListParams: {
        total: 0,
        page: 1,
        per_page: 25
    },
    currentBooking: null,
    loading: false,
    importedBookings: [],
    jobStatus: {
        jobId: null,
        status: null,
        progress: 0,
        error: null
    },
    deleteBookingPaymentLoading: false,
};

const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        getBookings: (state) => {
            state.loading = true;
        },
        getBookingsSuccess: (state, action) => {
            const { data, ...params } = action.payload;
            state.bookingsListParams = params;
            state.bookings = data;
            state.loading = false;
        },
        getBookingsFailed: (state) => {
            state.loading = false;
        },
        getBookingById: (state) => {
            state.loading = true;
        },
        getBookingByIdSuccess: (state, action) => {
            state.currentBooking = action.payload;
            state.loading = false;
        },
        getBookingByIdFailed: (state) => {
            state.loading = false;
        },
        getBookingByShowId: (state) => {
            state.loading = true;
        },
        getBookingByShowIdSuccess: (state, action) => {
            state.bookings = action.payload;
            state.loading = false;
        },
        getBookingByShowIdFailed: (state) => {
            state.loading = false;
        },
        getBookingByShortId: (state) => {
            state.loading = true;
        },
        getBookingByShortIdSuccess: (state, action) => {
            state.currentBooking = action.payload;
            state.loading = false;
        },
        getBookingByShortIdFailed: (state) => {
            state.loading = false;
        },
        createManualBooking: (state) => {
            state.loading = true;
        },
        createManualBookingSuccess: (state, action) => {
            state.bookings = [action.payload, ...state.bookings];
            state.loading = false;
        },
        createManualBookingFailed: (state) => {
            state.loading = false;
        },
        updateBookingStatus: (state) => {
            state.loading = true;
        },
        updateBookingStatusSuccess: (state, action) => {
            const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
            if (index !== -1) {
                state.bookings[index] = action.payload;
            }
            state.loading = false;
        },
        updateBookingStatusFailed: (state) => {
            state.loading = false;
        },
        updateBooking: (state) => {
            state.loading = true;
        },
        updateBookingSuccess: (state, action) => {
            const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
            if (index !== -1) {
                state.bookings[index] = action.payload;
            }
            state.loading = false;
        },
        updateBookingFailed: (state) => {
            state.loading = false;
        },
        cancelBooking: (state) => {
            state.loading = true;
        },
        cancelBookingSuccess: (state, action) => {
            const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
            if (index !== -1) {
                state.bookings[index] = action.payload;
            }
            state.loading = false;
        },
        cancelBookingFailed: (state) => {
            state.loading = false;
        },
        deleteBooking: (state) => {
            state.loading = true;
        },
        deleteBookingSuccess: (state, action) => {
            state.bookings = state.bookings.filter(booking => booking._id !== action.payload);
            state.loading = false;
        },
        deleteBookingFailed: (state) => {
            state.loading = false;
        },
        getBookingsByDateRange: (state) => {
            state.loading = true;
        },
        getBookingsByDateRangeSuccess: (state, action) => {
            state.bookings = action.payload;
            state.loading = false;
        },
        getBookingsByDateRangeFailed: (state) => {
            state.loading = false;
        },
        checkIn: (state) => {
            state.loading = true;
        },
        checkInSuccess: (state, action) => {
            const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
            if (index !== -1) {
                state.bookings[index] = action.payload;
            }
            state.loading = false;
        },
        checkInFailed: (state) => {
            state.loading = false;
        },
        importBookings: (state) => {
            state.importedBookings = [];
            state.loading = true;
        },
        importBookingsSuccess: (state, action) => {
            state.importedBookings = action.payload;
            state.loading = false;
            state.jobStatus.jobId = action.payload.jobId;
        },
        importBookingsFailed: (state) => {
            state.importedBookings = [];
            state.loading = false;
        },
        updateJobStatus: (state, action) => {
            state.jobStatus = {
                ...state.jobStatus,
                ...action.payload
            };
        },
        deleteBookingPayment: (state) => {
            state.deleteBookingPaymentLoading = true;
        },
        deleteBookingPaymentSuccess: (state, action) => {
            const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
            if (index !== -1) {
                console.log(action.payload);
                state.bookings[index] = action.payload;
            }
            state.deleteBookingPaymentLoading = false;
        },  
        deleteBookingPaymentFailed: (state) => {
            state.deleteBookingPaymentLoading = false;
        },
    },
});

export const {
    getBookings,
    getBookingsSuccess,
    getBookingsFailed,
    getBookingById,
    getBookingByIdSuccess,
    getBookingByIdFailed,
    getBookingByShowId,
    getBookingByShowIdSuccess,
    getBookingByShowIdFailed,
    getBookingByShortId,
    getBookingByShortIdSuccess,
    getBookingByShortIdFailed,
    createManualBooking,
    createManualBookingSuccess,
    createManualBookingFailed,
    updateBookingStatus,
    updateBookingStatusSuccess,
    updateBookingStatusFailed,
    updateBooking,
    updateBookingSuccess,
    updateBookingFailed,
    cancelBooking,
    cancelBookingSuccess,
    cancelBookingFailed,
    deleteBooking,
    deleteBookingSuccess,
    deleteBookingFailed,
    getBookingsByDateRange,
    getBookingsByDateRangeSuccess,
    getBookingsByDateRangeFailed,
    checkIn,
    checkInSuccess,
    checkInFailed,
    importBookings,
    importBookingsSuccess,
    importBookingsFailed,
    updateJobStatus,
    deleteBookingPayment,
    deleteBookingPaymentSuccess,
    deleteBookingPaymentFailed,
} = bookingSlice.actions;

export default bookingSlice.reducer;